import { useEffect, useState } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { LEAVE_TYPE_OPTIONS } from '../constants';
import type { LeaveType, SubmitLeavePayload } from '../types';

interface SubmitLeaveModalProps {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: SubmitLeavePayload) => void;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

type FormState = { type: LeaveType; from_date: string; to_date: string; reason: string };

export function SubmitLeaveModal({ open, loading, onClose, onSubmit }: SubmitLeaveModalProps) {
  const [form, setForm] = useState<FormState>({ type: 'annual', from_date: todayISO(), to_date: todayISO(), reason: '' });

  useEffect(() => {
    if (open) setForm({ type: 'annual', from_date: todayISO(), to_date: todayISO(), reason: '' });
  }, [open]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const invalidRange = new Date(form.to_date) < new Date(form.from_date);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (invalidRange) return;
    onSubmit({ type: form.type, from_date: form.from_date, to_date: form.to_date, reason: form.reason.trim() });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Submit Leave Request"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="submit-leave-form" loading={loading} disabled={invalidRange}>
            Submit Request
          </Button>
        </>
      }
    >
      <form id="submit-leave-form" onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Leave Type"
          name="leave-type"
          value={form.type}
          onChange={(e) => set('type', e.target.value as LeaveType)}
          options={LEAVE_TYPE_OPTIONS}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="From *"
            name="leave-from"
            type="date"
            value={form.from_date}
            onChange={(e) => set('from_date', e.target.value)}
            required
          />
          <Input
            label="To *"
            name="leave-to"
            type="date"
            value={form.to_date}
            onChange={(e) => set('to_date', e.target.value)}
            error={invalidRange ? 'End date must be on/after start date' : undefined}
            required
          />
        </div>
        <div>
          <label htmlFor="leave-reason" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Reason
          </label>
          <textarea
            id="leave-reason"
            rows={3}
            value={form.reason}
            onChange={(e) => set('reason', e.target.value)}
            placeholder="Brief reason for leave…"
            className="w-full resize-none rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
      </form>
    </Modal>
  );
}
